import { useState } from 'react';
import { 
  Home, 
  Users, 
  FileText, 
  DollarSign, 
  Settings, 
  BarChart3, 
  Activity,
  Crown,
  Plus,
  Minus,
  Calendar,
  Tag,
  CreditCard
} from 'lucide-react';

interface SalepageSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  subItems?: NavItem[];
}

export default function SalepageSidebar({ 
  activeSection, 
  setActiveSection
}: SalepageSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'ภาพรวม',
      icon: Home,
    },
    {
      id: 'users',
      label: 'ผู้ใช้',
      icon: Users,
    },
    {
      id: 'courses',
      label: 'คอร์ส',
      icon: FileText,
    },
    {
      id: 'revenue',
      label: 'รายได้',
      icon: DollarSign,
    },
    {
      id: 'discounts',
      label: 'จัดการส่วนลด',
      icon: Tag,
    },
    {
      id: 'payment-settings',
      label: 'ตั้งค่าการชำระเงิน',
      icon: CreditCard,
    },
    {
      id: 'management',
      label: 'การจัดการ',
      icon: Settings,
    },
    {
      id: 'monitoring',
      label: 'การตรวจสอบ',
      icon: Activity,
    },
    {
      id: 'reports',
      label: 'รายงาน',
      icon: BarChart3,
    },
  ];

  const renderNavItem = (item: NavItem, level = 0) => {
    const isActive = activeSection === item.id || !!item.subItems?.some(sub => sub.id === activeSection);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedSections.has(item.id);

    const itemBaseClass = 'relative w-full group';

    return (
      <div key={item.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              if (hasSubItems) {
                setActiveSection(item.subItems![0].id);
              } else {
                setActiveSection(item.id);
              }
            }}
            className={`${itemBaseClass} flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 active:scale-[0.99] ${
              isActive
                ? 'bg-gradient-to-r from-emerald-600/10 to-teal-600/5 text-emerald-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${level > 0 ? 'pl-3 pr-3 py-2' : ''} ${hasSubItems ? 'flex-1' : ''}`}
          >
            {isActive && level === 0 && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1.5 rounded-r-full bg-gradient-to-b from-emerald-600 to-teal-600" />
            )}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                isActive ? 'bg-white/70 shadow-sm' : 'bg-transparent group-hover:bg-white/60'
              }`}>
                <item.icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} />
              </div>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
            {item.badge && (
              <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-1 rounded-full">
                {item.badge}
              </span>
            )}
          </button>
          
          {hasSubItems && level === 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(item.id);
              }}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              {isExpanded ? (
                <Minus className="w-4 h-4 text-gray-600" />
              ) : (
                <Plus className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
        </div>
        
        {hasSubItems && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.subItems!.map(subItem => renderNavItem(subItem, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-72 h-full">
      <div className="sticky top-[84px]">
        <div className="rounded-2xl border border-white/40 bg-white/70 backdrop-blur-xl shadow-xl shadow-black/5 overflow-hidden">
          <div className="p-5 border-b border-gray-100/70">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 leading-tight">Admin</h2>
                <p className="text-xs text-gray-500">Salepage System</p>
              </div>
            </div>
          </div>

          <div className="p-3 max-h-[calc(100vh-140px)] overflow-y-auto">
            <nav className="space-y-1">
              {navItems.map(item => renderNavItem(item))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
